/**
 * Where to send the shopper after a Google sign-in, for pages that want them
 * back (the callback otherwise goes home). Kept briefly in sessionStorage,
 * since the round trip through Cognito leaves the site.
 */
const KEY = 'reebelo_auth_return_to';
const TTL_MS = 15 * 60 * 1000;

/** Only same-site paths, never another origin. */
const isSafePath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

export const setAuthReturnTo = (path: string): void => {
  if (!isSafePath(path)) {
    return;
  }

  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage blocked: the callback falls back to its default.
  }
};

/**
 * Forgets the stored path, for a page leaving without a sign-in: the Google
 * round trip is a full-page navigation, which never gets here.
 */
export const clearAuthReturnTo = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked: nothing was stored either.
  }
};

/** The stored path, once: it is cleared as it is read. */
export const takeAuthReturnTo = (): string | null => {
  try {
    const raw = sessionStorage.getItem(KEY);

    sessionStorage.removeItem(KEY);

    if (!raw) {
      return null;
    }

    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };

    return isSafePath(path) &&
      typeof at === 'number' &&
      Date.now() - at < TTL_MS
      ? path
      : null;
  } catch {
    return null;
  }
};
